import { Component, ViewChild, OnDestroy } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { AuthService, UIOrg } from 'src/app/auth/service/auth.service';
import { WorkforceLayoutService } from './workforce-layout.service';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { ClipboardService } from 'ngx-clipboard';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';

@Component({
    selector: 'app-root-workforce',
    templateUrl: './app.wf.component.html',
    styleUrls: ['./app.wf.component.scss'],
    standalone: false
})
export class WorkforceAppComponent implements OnDestroy {
  @ViewChild(MatSidenav) sidenav: MatSidenav;
  isSmallScreen: boolean = false;
  thinSidenavOpened: boolean = true;
  userInitial: string | undefined;
  orgs: UIOrg[] = [];
  $selectedOrg: Observable<UIOrg | undefined>;
  selectedOrg: UIOrg | undefined;
  $isOrgAdmin: Observable<boolean> = of(false);
  private layoutSubscription: Subscription;
  private selectedOrgSubscription: Subscription;

  constructor(
    public authService: AuthService,
    public layoutService: WorkforceLayoutService,
    private darkModeService: DarkModeService,
    private clipboard: ClipboardService,
    private notify: NotificationService,
    private router: Router
  ) {
    // responsive thin sidenav; logic belongs here b/c it needs to be ready before the component is init'ed
    this.layoutSubscription = this.layoutService.isSmallScreen$.subscribe((isSmall) => {
      this.isSmallScreen = isSmall;
      if (this.isSmallScreen) {
        this.thinSidenavOpened = false;
        if (this.sidenav) {
          this.sidenav.mode = 'over';
          this.sidenav.close();
        }
      } else {
        this.thinSidenavOpened = true;
        if (this.sidenav) {
          this.sidenav.mode = 'side';
          this.sidenav.open();
        }
      }
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    });
  }

  ngOnInit() {
    this.darkModeService.initializeTheme(); // we need to call it here b/c WF doesn't have a DarkModeToggleComponent which calls theme init
    this.userInitial = this.authService.getUserValue?.name
      ?.split(' ')
      .slice(0, 2)
      .map((n) => n.charAt(0).toUpperCase())
      .join('');
    this.$isOrgAdmin = this.authService.$isOrgAdmin;

    this.authService.$getOrgs.subscribe((orgs) => {
      this.orgs = orgs;
    });
    this.$selectedOrg = this.authService.$getSelectedOrg;
    this.selectedOrgSubscription = this.$selectedOrg.subscribe((org) => {
      this.selectedOrg = org;
    });
  }

  // close sidenav when clicking on certain nav buttons on mobile
  // so it doesn't stay annoyingly open
  onNavButtonClick() {
    if (this.isSmallScreen && this.sidenav) {
      this.sidenav.close();
    }
  }

  toggleThinSidenav() {
    if (this.sidenav) {
      this.sidenav.toggle();
      this.thinSidenavOpened = this.sidenav.opened;
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    }
  }

  // Open the chat history drawer (lives in the chat page). Navigate to /chat first
  // if we're elsewhere so the drawer has somewhere to open.
  openChatHistory() {
    const onChatPage = this.router.url.split(/[?#]/)[0] === '/chat';
    if (onChatPage) {
      this.layoutService.requestOpenChatHistory();
    } else {
      // The chat component isn't mounted yet, so flag the request for it to pick up on init.
      this.layoutService.setPendingOpenChatHistory();
      this.router.navigate(['/chat']);
    }
    this.onNavButtonClick();
  }

  switchOrg(orgId: string) {
    this.authService.setSelectedOrgId(orgId);
  }

  copyOrgId(orgId: string, event: Event) {
    event.stopPropagation();
    this.clipboard.copyFromContent(orgId);
    this.notify.success('Copied Org ID to clipboard');
  }

  showIntercom() {
    (window as any)['Intercom']('showNewMessage');
  }

  ngOnDestroy() {
    this.layoutSubscription?.unsubscribe();
    this.selectedOrgSubscription?.unsubscribe();
  }
}
